import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, type User } from "./firebase";
import { getTokenUser, getValidToken } from "./token-store";
import { apiGet } from "./api";

export interface TokenUser {
  email: string;
  uid: string;
  displayName: string | null;
  photoURL: null;
  getIdToken: () => Promise<string>;
}

interface AuthContextValue {
  user: User | TokenUser | null;
  loading: boolean;
  pendingQuestionnaire: boolean | null;
  setPendingQuestionnaire: (value: boolean) => void;
  pendingReferral: boolean | null;
  setPendingReferral: (value: boolean) => void;
}

function buildTokenUser(): TokenUser | null {
  const info = getTokenUser();
  if (!info) return null;
  return {
    ...info,
    photoURL: null,
    getIdToken: async () => {
      const token = await getValidToken();
      if (!token) throw new Error("Token expired");
      return token;
    },
  };
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  pendingQuestionnaire: null,
  setPendingQuestionnaire: () => {},
  pendingReferral: null,
  setPendingReferral: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | TokenUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingQuestionnaire, setPendingQuestionnaire] = useState<boolean | null>(null);
  const [pendingReferral, setPendingReferral] = useState<boolean | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const resolvedUser = firebaseUser ?? buildTokenUser();
      setUser(resolvedUser);
      if (!resolvedUser) {
        setPendingQuestionnaire(null);
        setPendingReferral(null);
        setLoading(false);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    apiGet<{ pendingQuestionnaire: boolean; pendingReferral?: boolean }>("/api/me")
      .then((data) => {
        if (!cancelled) {
          setPendingQuestionnaire(data.pendingQuestionnaire);
          setPendingReferral(data.pendingReferral ?? false);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPendingQuestionnaire(true);
          setPendingReferral(false);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, pendingQuestionnaire, setPendingQuestionnaire, pendingReferral, setPendingReferral }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
